import { CheckCircle2, Circle } from 'lucide-react';
import { getAktePhasen } from '@/lib/akte-phasen';
import { formatHauptphaseDatum } from '@/lib/akten-hauptphase';

interface AktenProgressbarProps {
  hauptphase: number | null | undefined;
  hauptphase_datum: string | null | undefined;
  betriebId?: string;
  onPhaseClick?: (phase: number) => void;
  readonly?: boolean;
}

export function AktenProgressbar({
  hauptphase,
  hauptphase_datum,
  betriebId = 'gutachten',
  onPhaseClick,
  readonly = false,
}: AktenProgressbarProps) {
  const phasen = getAktePhasen(betriebId);
  const phaseCount = phasen.length;
  const activePhase = Math.min(Math.max(Number(hauptphase ?? 1), 1), phaseCount);
  const datumStr = formatHauptphaseDatum(hauptphase_datum);

  return (
    <div
      data-testid="akten-progressbar"
      className="flex items-center gap-2 w-full"
    >
      {/* ─── Desktop: horizontale Linie mit Knoten ─── */}
      <div className="hidden sm:flex items-center flex-1 gap-0 min-w-0">
        {phasen.map((phase, idx) => {
          const phaseNr = idx + 1;
          const isDone   = phaseNr < activePhase;
          const isActive = phaseNr === activePhase;
          const isFuture = phaseNr > activePhase;

          const nodeClasses = [
            'flex flex-col items-center gap-0.5 relative',
            readonly ? '' : 'cursor-pointer group',
          ].join(' ');

          const circleClasses = [
            'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
            isDone   ? 'bg-green-500 border-green-500 text-white'  : '',
            isActive ? 'bg-blue-600 border-blue-600 text-white shadow-sm' : '',
            isFuture ? 'bg-white border-gray-300 text-gray-300'   : '',
            !readonly ? 'group-hover:scale-110' : '',
          ].join(' ');

          const labelClasses = [
            'text-[9px] font-medium whitespace-nowrap',
            isDone   ? 'text-green-600' : '',
            isActive ? 'text-blue-700 font-semibold' : '',
            isFuture ? 'text-gray-400' : '',
          ].join(' ');

          return (
            <div key={phase.id} className="flex items-center min-w-0">
              <button
                type="button"
                data-testid={`phase-node-${phaseNr}`}
                aria-label={`Phase ${phaseNr}: ${phase.label}${isActive ? ' (aktiv)' : ''}`}
                aria-current={isActive ? 'step' : undefined}
                disabled={readonly}
                onClick={() => onPhaseClick?.(phaseNr)}
                className={nodeClasses}
              >
                <span className={circleClasses}>
                  {isDone
                    ? <CheckCircle2 className="w-3 h-3" />
                    : <Circle className="w-3 h-3" />}
                </span>
                <span className={labelClasses}>{phase.label}</span>
              </button>

              {/* Verbindungslinie zwischen Knoten */}
              {idx < phaseCount - 1 && (
                <div
                  className={[
                    'flex-1 h-0.5 min-w-[8px] mx-0.5',
                    phaseNr < activePhase ? 'bg-green-400' : 'bg-gray-200',
                  ].join(' ')}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* ─── Mobil: Punkte + Label der aktiven Phase ─── */}
      <div className="flex sm:hidden items-center gap-1.5">
        <div className="flex items-center gap-1">
          {phasen.map((phase, idx) => {
            const phaseNr = idx + 1;
            const isDone   = phaseNr < activePhase;
            const isActive = phaseNr === activePhase;
            return (
              <button
                key={phase.id}
                type="button"
                data-testid={`phase-dot-${phaseNr}`}
                aria-label={`Phase ${phaseNr}: ${phase.label}`}
                disabled={readonly}
                onClick={() => onPhaseClick?.(phaseNr)}
                className={[
                  'w-2 h-2 rounded-full transition-all',
                  isDone   ? 'bg-green-500' : '',
                  isActive ? 'bg-blue-600 w-3 h-3' : '',
                  !isDone && !isActive ? 'bg-gray-300' : '',
                  !readonly ? 'cursor-pointer' : 'cursor-default',
                ].join(' ')}
              />
            );
          })}
        </div>
        <span className="text-[11px] font-semibold text-blue-700 ml-1">
          {phasen[activePhase - 1]?.label}
        </span>
      </div>

      {/* ─── Datum des letzten Phasenwechsels ─── */}
      {datumStr && (
        <span
          className="text-[10px] text-gray-400 flex-shrink-0 ml-1"
          title="Datum des letzten Phasenwechsels"
        >
          {datumStr}
        </span>
      )}
    </div>
  );
}
